import {
  AtSymbolIcon,
  BookOpenIcon,
  ChatAlt2Icon,
  DocumentSearchIcon,
  EyeOffIcon,
  GiftIcon,
  GlobeAltIcon,
  LightningBoltIcon,
  OfficeBuildingIcon,
  RefreshIcon,
  ShieldCheckIcon,
  SupportIcon,
  UsersIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import SlackChat from '../mocks/SlackChat';
import SlackSetup from '../mocks/SlackSetup';
import SlackThread from '../mocks/SlackThread';
import type { ChannelContent, DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';

const BRAND: DemoBrand = {
  name: 'Atlas',
  initial: 'A',
  bg: 'bg-[#ea580c]',
};

const WORKSPACE = 'Northwind';
const CHANNEL = 'ask-people-ops';

/*
 * Facts this page relies on (checked 2026-10-21, docs/agents/slack.mdx and
 * the Slack card in /api/bots/integrations): Slack is in FREE_PLAN_CHANNELS;
 * install is Slack's OAuth screen (pick workspace + channel, Allow); the
 * agent answers every DM and channel @mentions in a thread; /clear resets a
 * person's history; several workspaces each with a pause switch; Slack
 * threads are NOT listed in Agents → Conversations.
 */
export function useSlackContent(): ChannelContent {
  const { t } = useChannelsT();
  const { t: tc } = useLocale('chatbots');

  return {
    key: 'slack',
    name: 'Slack',
    logo: 'FaSlack',
    seoTitle: t(
      'Slack AI Chatbot: An AI Agent That Answers Your Team in Slack',
    ),
    seoDescription: t(
      'Add an AI agent to Slack that answers your team from your handbook, policies and docs, in direct messages and channels. Works on the Free plan.',
    ),
    hero: {
      label: t('Slack AI chatbot'),
      title: t('Answers for your team, right in Slack.'),
      accent: t('No more digging through docs.'),
      lead: t(
        'Train an agent on your handbook, policies and guides, add it to Slack, and anyone can ask it in a direct message or @mention it in a channel.',
      ),
      facts: [
        t('Works on the Free plan'),
        t('Direct messages and @mentions'),
        t('Answers from your own docs'),
      ],
      description: t(
        'Example: an employee asks the company’s Slack agent about the meal allowance on a work trip and whether she can book the conference hotel. The agent answers both from the travel policy.',
      ),
      demo: (
        <SlackChat
          brand={BRAND}
          person={{ name: 'Priya Shah', initials: 'PS' }}
          lines={[
            {
              from: 'person',
              text: t('What’s the daily meal allowance on a work trip?'),
              time: '9:41',
              at: 600,
            },
            {
              from: 'agent',
              text: t(
                'It’s €45 a day in Europe and €60 everywhere else. Keep your receipts and add them to your expense report within 30 days.',
              ),
              time: '9:41',
              typingAt: 1100,
              at: 2100,
            },
            {
              from: 'person',
              text: t(
                'Can I book the conference hotel if it’s over the limit?',
              ),
              time: '9:42',
              at: 3200,
            },
            {
              from: 'agent',
              text: t(
                'Yes, with your manager’s approval. It’s in the travel policy, section 4.2.',
              ),
              time: '9:42',
              typingAt: 3600,
              at: 4500,
            },
          ]}
        />
      ),
      steps: [
        {
          icon: DocumentSearchIcon,
          title: t('Searched the travel policy'),
          via: tc('Knowledge'),
          at: 1500,
        },
        {
          icon: BookOpenIcon,
          title: t('Found section 4.2'),
          via: tc('Knowledge'),
          at: 3900,
        },
      ],
    },
    jobs: {
      title: t('The questions your team asks every week.'),
      tail: t('Answered in seconds.'),
      lead: t(
        'Policies, how-tos and where things are. Your agent answers from the documents you give it, so people get an answer without waiting for a colleague.',
      ),
      items: [
        {
          icon: UsersIcon,
          title: t('HR and policies'),
          detail: t(
            'Leave, benefits, expenses and travel rules, straight from your handbook.',
          ),
        },
        {
          icon: SupportIcon,
          title: t('IT how-tos'),
          detail: t(
            'Wi-Fi, VPN, new laptops and passwords, from your IT guides.',
          ),
        },
        {
          icon: BookOpenIcon,
          title: t('Product knowledge'),
          detail: t(
            'Specs, pricing and positioning for sales and support, from your product docs.',
          ),
        },
        {
          icon: AtSymbolIcon,
          title: t('Answers in channels'),
          detail: t(
            '@mention it in any channel it’s in, and it replies in a thread.',
          ),
        },
        {
          icon: GlobeAltIcon,
          title: t('Speaks every language'),
          detail: t(
            'Replies in the language each person writes in, from the same documents.',
          ),
        },
        {
          icon: LightningBoltIcon,
          title: t('Checks your systems'),
          detail: t(
            'Looks up an order or a booking in your own system with a custom action.',
          ),
        },
      ],
    },
    setup: {
      title: t('Add it to Slack in a minute.'),
      tail: t('No code, no bot tokens.'),
      steps: [
        {
          title: t('Build your agent'),
          detail: t(
            'Upload your handbook, guides and docs, and tell it where to send people when it doesn’t know.',
          ),
        },
        {
          title: t('Allow it in Slack'),
          detail: t(
            'Click Connect on Slack, pick your workspace and the channel it posts in, and click Allow.',
          ),
        },
        {
          title: t('Ask it something'),
          detail: t(
            'Find the app under Apps in Slack and send it a message, or add it to a channel and @mention it.',
          ),
        },
      ],
      guide: 'https://easy-peasy.ai/docs/agents/slack',
      guideLabel: t('Read the Slack setup guide'),
      description: t(
        'Slack’s window asking to let Easy-Peasy.AI into the Northwind workspace and choose the channel it posts in, next to the agent’s list of connected workspaces.',
      ),
      visual: <SlackSetup workspace={WORKSPACE} channel={CHANNEL} />,
    },
    spotlight: {
      title: t('Ask it in any channel.'),
      tail: t('It answers in a thread.'),
      lead: t(
        '@mention your agent in a channel and it replies in a thread under the question, so the answer is there for everyone and the channel stays tidy.',
      ),
      points: [
        t('Direct messages: it answers every message'),
        t('Channels: it answers when @mentioned'),
        t('Each person’s conversation keeps its history'),
        t('Anyone can start over by typing /clear'),
      ],
      description: t(
        'A Slack channel where an employee @mentions the agent with a question about payslips, and the thread where the agent answers.',
      ),
      visual: (
        <SlackThread
          brand={BRAND}
          channel={CHANNEL}
          asker={{ name: 'Tom Becker', initials: 'TB' }}
          question={t('when do December payslips go out?')}
          answer={t(
            'On 19 December, a few days earlier than usual because of the holidays. You’ll find them in the payroll portal.',
          )}
        />
      ),
    },
    rules: {
      title: t('Good to know.'),
      tail: t('Slack, in plain words.'),
      items: [
        {
          icon: GiftIcon,
          title: t('Free plan included'),
          detail: t(
            'Slack works on every plan, including Free. Actions, such as bookings, need a paid plan.',
          ),
        },
        {
          icon: ChatAlt2Icon,
          title: t('Messages and mentions'),
          detail: t(
            'In direct messages it answers every message. In channels it answers when someone @mentions it, in a thread.',
          ),
        },
        {
          icon: OfficeBuildingIcon,
          title: t('More than one workspace'),
          detail: t(
            'Connect several workspaces and pause each one with its own switch.',
          ),
        },
        {
          icon: ShieldCheckIcon,
          title: t('Permission to install'),
          detail: t(
            'You need permission to install apps in the workspace, or an admin who approves the request.',
          ),
        },
        {
          icon: EyeOffIcon,
          title: t('Chats stay in Slack'),
          detail: t(
            'Slack conversations aren’t listed in your agent’s Conversations page. You read them in Slack.',
          ),
        },
        {
          icon: RefreshIcon,
          title: t('Start over with /clear'),
          detail: t(
            'Anyone can type /clear to clear their conversation history with the agent.',
          ),
        },
      ],
    },
    faqs: [
      {
        question: t('Is the Slack AI chatbot free?'),
        answer: t(
          'Yes. Slack works on every plan, including Free. Actions such as bookings and collecting details are on paid plans.',
        ),
      },
      {
        question: t('What can our team ask it?'),
        answer: t(
          'Anything covered by the knowledge you give it: your handbook, IT guides, benefits and product docs. Tell it in the instructions where to send people when it doesn’t know, for example a help desk channel.',
        ),
      },
      {
        question: t('Does it answer in channels?'),
        answer: t(
          'Yes, when someone @mentions it. It replies in a thread so the channel stays tidy. In direct messages it answers every message.',
        ),
      },
      {
        question: t('Can I add it to more than one workspace?'),
        answer: t(
          'Yes. Connect each workspace from the Slack card. Every workspace has its own switch to pause the agent there.',
        ),
      },
      {
        question: t('Do I need to be a Slack admin?'),
        answer: t(
          'You need permission to install apps in your workspace, or a workspace admin who approves the request.',
        ),
      },
      {
        question: t('Where can I read the conversations?'),
        answer: t(
          'In Slack itself. Slack conversations aren’t listed in your agent’s Conversations page, unlike website, WhatsApp, Messenger, Telegram and LINE chats.',
        ),
      },
      {
        question: t('Is this the same as Slack AI?'),
        answer: t(
          'No. Slack AI is Slack’s own set of features for summarizing and searching your Slack messages. Your agent answers from the documents you give it, and the same agent can also answer on your website and other channels.',
        ),
      },
      {
        question: t('Which languages does it speak?'),
        answer: t(
          'It replies in the language each person writes in, more than 80 of them, from the same knowledge.',
        ),
      },
    ],
    finalTitle: t('Your team could be asking your agent by lunchtime.'),
    finalExchange: {
      question: t('How many vacation days do I have left?'),
      answer: t(
        'I can’t see your balance, but the HR portal shows it under Time off.',
      ),
    },
  };
}
